import type { Job, Timesheet } from '@rendimees24/shared-types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { collectionGroup, onSnapshot, query, where } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { useAuth } from '../auth/AuthContext';

import { getFirebase } from './firebase';
import {
  checkIn,
  fetchWorkerJob,
  pruneWorkerJobCopies,
  subscribeJobTimesheets,
  subscribeWorkerJobs,
  type CheckInPayload,
} from './worker';

export function useWorkerJobs(uid: string | undefined) {
  const [snapshot, setSnapshot] = useState<{
    uid: string;
    jobs: Job[];
    error: Error | null;
  } | null>(null);
  useEffect(() => {
    if (!uid) return;
    let live = true;
    const unsub = subscribeWorkerJobs(
      uid,
      (jobs, meta) => {
        if (!live) return;
        setSnapshot({ uid, jobs, error: null });
        // The server's list (not a cache snapshot): copies of jobs no longer
        // on it are dropped, so a removed assignment does not open offline.
        if (meta && !meta.fromCache)
          pruneWorkerJobCopies(
            uid,
            jobs.map((j) => j.id),
          );
      },
      (error) => {
        if (live) setSnapshot({ uid, jobs: [], error });
      },
    );
    return () => {
      live = false;
      unsub();
    };
  }, [uid]);
  const current = snapshot?.uid === uid ? snapshot : null;
  return {
    jobs: !uid ? [] : (current?.jobs ?? null),
    loading: !!uid && !current,
    error: current?.error ?? null,
  };
}

/** Own history remains readable after assignment removal; no parent-job read is granted. */
export function useMyTimesheetsAcrossJobs(uid: string | undefined) {
  const [state, setState] = useState<{
    uid: string;
    timesheets: Timesheet[];
    error: Error | null;
  } | null>(null);
  useEffect(() => {
    if (!uid) return;
    let live = true;
    const q = query(
      collectionGroup(getFirebase().firestore, 'timesheets'),
      where('workerId', '==', uid),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        if (live)
          setState({
            uid,
            error: null,
            timesheets: snap.docs
              .map(
                (d) =>
                  ({
                    ...d.data(),
                    id: d.id,
                    jobId: d.ref.parent.parent?.id ?? d.data().jobId,
                  }) as Timesheet,
              )
              .sort((a, b) =>
                (b.entries?.[0]?.checkInAt ?? '').localeCompare(a.entries?.[0]?.checkInAt ?? ''),
              ),
          });
      },
      (error) => {
        if (live) setState({ uid, timesheets: [], error });
      },
    );
    return () => {
      live = false;
      unsub();
    };
  }, [uid]);
  const current = state?.uid === uid ? state : null;
  return {
    timesheets: current?.timesheets ?? [],
    loading: !!uid && !current,
    error: current?.error ?? null,
  };
}

/**
 * One job's timesheets for this worker — plain subcollection query (robust
 * against the collectionGroup rule/index pitfalls). Used by the worker job page.
 */
export function useJobTimesheets(jobId: string | undefined, uid: string | undefined) {
  const [snapshot, setSnapshot] = useState<{
    uid: string;
    jobId: string;
    timesheets: Timesheet[];
    error: Error | null;
  } | null>(null);
  useEffect(() => {
    if (!jobId || !uid) return;
    return subscribeJobTimesheets(
      jobId,
      uid,
      (timesheets) => setSnapshot({ uid, jobId, timesheets, error: null }),
      (error) => setSnapshot({ uid, jobId, timesheets: [], error }),
    );
  }, [jobId, uid]);
  const current = snapshot?.uid === uid && snapshot?.jobId === jobId ? snapshot : null;
  return {
    timesheets: current?.timesheets ?? [],
    loading: !!uid && !!jobId && !current,
    error: current?.error ?? null,
  };
}

/**
 * The worker's job. `networkMode: 'always'`: React Query would otherwise
 * PAUSE the read offline — no data, no error, not loading — and the page fell
 * through to "Tellimust ei leitud". `fetchWorkerJob` serves the copy the
 * device holds when there is no network (UX 1C.3).
 */
export function useJob(jobId: string | undefined) {
  const { user } = useAuth();
  const uid = user?.uid;
  return useQuery({
    queryKey: ['job', jobId, uid],
    queryFn: () => (jobId && uid ? fetchWorkerJob(jobId, uid) : Promise.resolve(null)),
    enabled: Boolean(jobId && uid),
    networkMode: 'always',
  });
}

export function useCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    // The mutation writes its own durable offline queue. React Query must not
    // pause it before that function ever runs.
    networkMode: 'always',
    mutationFn: (p: CheckInPayload) => checkIn(p),
    onSuccess: (_, p) => {
      void qc.invalidateQueries({ queryKey: ['job', p.jobId] });
    },
  });
}

// EPIC 39 — useCheckOut removed: checking out + submitting is now the single
// `completeWorkDay` callable (services/timesheets.ts), which server-stamps the
// end time and applies the statutory auto-lunch.
